// Asking the engine to act. Every button in the app ends here: one inbox row naming one catalogue command, a toast that
// says it was sent, and — when the engine writes the row back (Realtime) — a toast with the engine's result or its refusal,
// verbatim. Nothing here marks anything done: the screens refresh from the engine's read models.
import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from "react";
import { useToast } from "../components";
import { inboxRow } from "../data/rows";
import { newIdempotencyKey } from "../data/source";
import type { InboxRow } from "../data/types";
import { mayRequest, validatePayload } from "../lib/commands";
import { onInboxRow, resultNote } from "../lib/settled";
import { useApp } from "./context";

export interface EngineRequest { command: string; engagementId: string | null; payload: Record<string, unknown>; what: string; idempotencyKey?: string }
type RequestFn = (r: EngineRequest) => Promise<InboxRow | null>;
const Ctx = createContext<RequestFn | null>(null);

export function useEngine(): RequestFn {
  const c = useContext(Ctx);
  if (!c) throw new Error("useEngine outside <EngineProvider>");
  return c;
}

const OUTCOME: Record<string, string> = { done: "Done by the engine", refused: "Refused by the engine", failed: "The engine failed", running: "The engine is running it", pending_signature: "Waiting for your signature" };

export function EngineProvider({ children }: { children: ReactNode }) {
  const { ds, me, catalogue } = useApp();
  const toast = useToast();
  const watching = useRef(new Map<string, string>());           // inbox id → what

  useEffect(() => ds.subscribe("inbox", (e) => {
    if (!e.row) return;
    const row = inboxRow(e.row);
    onInboxRow(row);                                                // an Action center item this act settles (lib/settled)
    const what = watching.current.get(row.id);
    if (!what || row.status === "pending") return;
    const body = row.status === "refused" || row.status === "failed" ? row.refusal ?? "No reason given."
      : row.status === "done" ? resultNote(row) ?? "The screens refresh from the record." : undefined;
    toast.push({ id: `inbox-${row.id}`, title: `${what} — ${OUTCOME[row.status] ?? row.status}`, body, state: row.status });
    if (row.status !== "running") watching.current.delete(row.id);
  }), [ds, toast]);

  const request = useCallback<RequestFn>(async (r) => {
    const spec = catalogue.get(r.command);
    const allowed = mayRequest(spec, me, r.engagementId ? "engagement" : "firm");
    if (!allowed.ok) { toast.push({ title: `${r.what} — not sent`, body: allowed.reason ?? undefined, state: "blocked", sticky: true }); return null; }
    const errs = validatePayload(spec!, r.payload);
    if (Object.keys(errs).length) {
      toast.push({ title: `${r.what} — not sent`, body: Object.entries(errs).map(([k, v]) => `${k}: ${v}`).join(" "), state: "blocked", sticky: true });
      return null;
    }
    try {
      const row = await ds.submit({ command: r.command, engagementId: r.engagementId, payload: r.payload, idempotencyKey: r.idempotencyKey ?? newIdempotencyKey(), mode: spec!.mode });
      watching.current.set(row.id, r.what);
      toast.push({ id: `inbox-${row.id}`, title: `${r.what} — sent to the engine`, body: "It shows here once the engine has run it through its gates.", state: "pending" });
      return row;
    } catch (e) {
      toast.push({ title: `${r.what} — not sent`, body: (e as Error).message, state: "failed", sticky: true });
      return null;
    }
  }, [catalogue, ds, me, toast]);

  return <Ctx.Provider value={request}>{children}</Ctx.Provider>;
}
